import type { GamificationAccount, GamificationTier } from '../api/CoreApi';
import { TRANSACTIONS_LIST_LIMIT } from '../components/Gamification/TransactionsList';
import {
  meGetGamificationAccountTransactions,
  type Transaction,
} from '../features/queries/meGetGamificationAccountTransactions';
import { meGetGamificationAccounts } from '../features/queries/meGetGamificationAccounts';
import { meGetGamificationTier } from '../features/queries/meGetGamificationTier';
import { useInfiniteQ, useQ } from '../lib/query';
import { __, toastError } from '../utils';
import { useMqtt } from './MqttClientProvider';
import { useAuth } from '@presov.sk/presov-auth-react';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';

const GENEREAL_ACCOUNT_TYPE = 'general';

export type InfiniteTransactionsData = {
  pages: {
    transactions: Transaction[];
    hasNextPage: boolean;
    nextCursor?: number;
  }[];
  pageParams: unknown[];
};

type GamificationContextType = {
  generalAccountPoints: number;
  generalAccountId?: number;
  gamificationTier?: GamificationTier['tier'];
  isLoadingPointAccounts?: boolean;
  pointAccounts: GamificationAccount[];
  addOptimisticTransaction: (transaction: Transaction) => void;
  updateGeneralAccountPoints: (accountId: number, points: number) => void;
};

const GamificationContext =
  React.createContext<GamificationContextType>(undefined);

const GamificationProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { authUser, authState } = useAuth();
  const { subscribe } = useMqtt();
  const queryClient = useQueryClient();

  const {
    data: pointAccounts = [],
    isLoading: isLoadingPointAccounts,
    isError: isErrorPointAccounts,
    refetch,
    queryKey: pointAccountsQueryKey,
  } = useQ(meGetGamificationAccounts, undefined, {
    enabled: authState === 'authorized',
  });

  const { data: gamificationTier } = useQ(meGetGamificationTier, undefined, {
    enabled: authState === 'authorized',
  });

  const generalAccountId = pointAccounts.find(
    (account) => account.account_type === GENEREAL_ACCOUNT_TYPE,
  )?.account_id;

  const { queryKey: generalAccountQueryKey } = useInfiniteQ(
    meGetGamificationAccountTransactions,
    {
      accountId: generalAccountId,
      limit: TRANSACTIONS_LIST_LIMIT,
    },
    {
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        if (!lastPage || !lastPage.nextCursor) return undefined;
        return lastPage.nextCursor;
      },
      enabled: false,
    },
  );

  const addOptimisticTransaction = React.useCallback(
    (transaction: Transaction) => {
      // Pridáme optimistickú transakciu do point accounts
      queryClient.setQueryData(
        pointAccountsQueryKey,
        (oldAccounts?: GamificationAccount[]) => {
          if (!oldAccounts) return oldAccounts;
          return oldAccounts.map((account) =>
            account.account_type === GENEREAL_ACCOUNT_TYPE
              ? {
                  ...account,
                  balance: (account.balance ?? 0) + transaction.points,
                }
              : account,
          );
        },
      );

      // Namiesto invalidateQueries použijeme setQueryData pre update bez straty infinite scroll stavu
      queryClient.setQueryData(
        generalAccountQueryKey,
        (oldData: InfiniteTransactionsData) => {
          // Typový guard pre infinite query data
          if (!oldData || !oldData.pages || oldData.pages.length === 0) {
            return oldData;
          }

          // Pridáme novú transakciu na začiatok prvej stránky
          const updatedPages = [...oldData.pages];
          updatedPages[0] = {
            ...updatedPages[0],
            transactions: [transaction, ...updatedPages[0].transactions],
          };

          return {
            ...oldData,
            pages: updatedPages,
          };
        },
      );
    },
    [generalAccountQueryKey, pointAccountsQueryKey, queryClient],
  );

  const updateGeneralAccountPoints = React.useCallback(
    (accountId: number, points: number) => {
      queryClient.setQueryData(
        pointAccountsQueryKey,
        (oldAccounts?: GamificationAccount[]) => {
          if (!oldAccounts) return oldAccounts;
          return oldAccounts.map((account) =>
            account.account_id === accountId
              ? {
                  ...account,
                  balance: (account.balance ?? 0) + points,
                }
              : account,
          );
        },
      );
    },
    [pointAccountsQueryKey, queryClient],
  );

  React.useEffect(() => {
    if (isErrorPointAccounts) {
      toastError(__('Nepodarilo sa načítať počet bodov.'));
    }
  }, [isErrorPointAccounts]);

  React.useEffect(() => {
    if (authUser?.clientAccountId) {
      subscribe(
        `digiobcan/user/${authUser.clientAccountId}/gamification/transactions`,
        (message) => {
          const transaction = JSON.parse(message) as Transaction;
          if (!transaction?.points) {
            return;
          }
          if (pointAccounts.length === 0) {
            refetch();
          }
          addOptimisticTransaction(transaction);
        },
        { qos: 1 },
      );
    }
  }, [
    authUser?.clientAccountId,
    subscribe,
    queryClient,
    addOptimisticTransaction,
    pointAccounts.length,
    refetch,
  ]);

  const generalAccountPoints =
    pointAccounts.find(
      (account) => account.account_type === GENEREAL_ACCOUNT_TYPE,
    )?.balance || 0;

  return (
    <GamificationContext.Provider
      value={{
        generalAccountPoints,
        generalAccountId,
        isLoadingPointAccounts,
        pointAccounts,
        addOptimisticTransaction,
        updateGeneralAccountPoints,
        gamificationTier,
      }}
    >
      {children}
    </GamificationContext.Provider>
  );
};

export default GamificationProvider;

export const useGamification = () => {
  const context = React.useContext(GamificationContext);
  if (!context) {
    throw new Error(
      'useGamification must be used within a GamificationProvider',
    );
  }
  return context;
};
